<script setup lang="ts">
import { computed, defineEmits, defineProps, ref, RenderFunction, watch } from 'vue'
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxLabel,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/vue'
import { CheckIcon, SelectorIcon } from '@heroicons/vue/solid'
import { ExclamationCircleIcon } from '@heroicons/vue/outline'
import { SelectOption } from '../../typings'
import { useFieldHelper } from './useBaseInput'

const props = defineProps<{
    modelValue: string
    label: string
    name: string
    icon?: RenderFunction
    required?: boolean
    required2?: boolean
    rules?: any
    options: SelectOption[]
    nullable?: boolean
    standalone?: boolean
}>()

const { handleChange, errorMessage } = useFieldHelper(props.name, props.rules, {
    standalone: props.standalone ? props.standalone : false,
    initialValue: props.modelValue,
})

const emit = defineEmits(['update:modelValue'])

watch(
    () => props.modelValue,
    (value) => {
        handleChange(value)
    }
)
function updateValue(value: SelectOption) {
    const val = value?.key ? value.key : null
    handleChange(val)
    emit('update:modelValue', val)
}

const selected = computed({
    get: () => props.options.find((option) => option.key === props.modelValue),
    set: (value) => updateValue(value),
})

const query = ref('')

watch(query, (newVal) => {
    if (!props.options.find((option) => option.label === newVal)) {
        updateValue(null)
    }
})

const filteredOptions = computed(() =>
    query.value === ''
        ? props.options
        : props.options.filter((option) =>
              option.label.toLowerCase().replace(/\s+/g, '').includes(query.value.toLowerCase().replace(/\s+/g, ''))
          )
)
</script>

<template>
    <Combobox v-model="selected" as="div">
        <ComboboxLabel
            class="block text-sm font-medium text-gray-700"
            :class="{ 'required': required, 'required-2': required2 }">
            {{ props.label }}
        </ComboboxLabel>
        <div class="relative mt-1">
            <div v-if="icon" class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Component :is="icon" class="h-5 w-5 text-gray-400" aria-hidden="true" />
            </div>
            <ComboboxInput
                class="block w-full rounded-md border-gray-300 py-3 pr-8 shadow-sm focus:border focus:border-primary focus:ring-primary disabled:bg-gray-100 sm:text-sm"
                :aria-invalid="!!errorMessage"
                :name="props.name"
                v-bind="$attrs"
                autocomplete="off"
                :class="{
                    'pl-10': !!icon,
                    'border-red-300 pr-14 text-red-900 placeholder-red-300 focus:border-red-500 focus:outline-none focus:ring-red-500':
                        errorMessage,
                }"
                :display-value="(option) => (option ? option.label : null)"
                @change="query = $event.target.value" />
            <div v-if="errorMessage" class="pointer-events-none absolute inset-y-0 right-4 flex items-center pr-3">
                <ExclamationCircleIcon class="h-5 w-5 text-red-500" aria-hidden="true" />
            </div>
            <ComboboxButton class="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
                <SelectorIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
            </ComboboxButton>

            <ComboboxOptions
                v-if="filteredOptions.length > 0"
                class="absolute z-10 mt-1 max-h-60 w-full list-none overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                <ComboboxOption
                    v-for="option in filteredOptions"
                    :key="option.key"
                    v-slot="{ active, selected }"
                    :value="option"
                    as="template">
                    <li
                        :class="[
                            'relative cursor-default select-none py-2 pl-3 pr-9',
                            active ? 'bg-primary text-white' : 'text-gray-900',
                        ]">
                        <span :class="['block truncate', selected && 'font-semibold']">
                            {{ option.label }}
                        </span>

                        <span
                            v-if="selected"
                            :class="[
                                'absolute inset-y-0 right-0 flex items-center pr-4',
                                active ? 'text-white' : 'text-primary',
                            ]">
                            <CheckIcon class="h-5 w-5" aria-hidden="true" />
                        </span>
                    </li>
                </ComboboxOption>
            </ComboboxOptions>
        </div>
        <p :id="props.name + '-error'" class="mt-2 text-sm text-red-600">{{ errorMessage }}</p>
    </Combobox>
</template>
